import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next, schedule } from '@ember/runloop';
import { service } from '@ember/service';

import moment from 'moment';

import type { PopupData, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';

import didUpdate from 'webapp/modifiers/did-update';
import BarLoaderComponent from 'webapp/components/bar-loader';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import MonitoringPanelsUpdatesItemComponent from 'webapp/components/monitoring-panels/updates/item';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

export default class AccountPopupProductUpdatesComponent extends PopupInnerModalComponent<PopupData.None> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('product-updates') declare productUpdatesService: Service.ProductUpdates;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.analyticsService.event(TrackingEvent.PRODUCT_UPDATES_POPUP_SHOWN);

		next(() => {
			this.productUpdatesService.saveProductUpdatesSeen();
		});
	}

	get isLoading(): boolean {
		return this.productUpdatesService.isLoading;
	}

	get model() {
		const timestampYearToDate = moment().subtract(1, 'year').unix();

		const model = this.productUpdatesService.productUpdates.filter(
			(productUpdate) => productUpdate.created >= timestampYearToDate,
		);

		return model;
	}

	@action
	seeAllUpdates(): void {
		window.open('https://databox.com/product-updates', '_blank');

		this.analyticsService.event(TrackingEvent.PRODUCT_UPDATES_CLICKED, {
			origin: 'see_all',
		});

		this.close();
	}

	@action
	override reposition(): void {
		schedule('afterRender', () => {
			this.args.model.reposition();
		});
	}
	<template>
		<div class="title" {{didUpdate this.reposition this.isLoading}}>
			<CommonUiIconComponent @name="Broadcast" @size="lg" />
			Product updates
		</div>

		{{#if this.isLoading}}
			<div class="content content--loading">
				<BarLoaderComponent />
			</div>
		{{else}}
			<div class="content">
				{{#each this.model as |item|}}
					<MonitoringPanelsUpdatesItemComponent
						@model={{item}}
						@originForProductUpdatesClicked="read_more"
					/>
				{{/each}}
			</div>

			<div class="controls2 controls2--sticky controls2--center">
				<SubmitButtonComponent
					@text="See all updates"
					@size="big"
					@noDelay={{true}}
					@onTrigger={{this.seeAllUpdates}}
				/>
			</div>
		{{/if}}
	</template>
}
